import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';

import { Auth, GoogleAuthProvider, signInWithPopup } from '@angular/fire/auth';
import { Observable, from } from 'rxjs';
import { environment } from '../../environments/environment';
import { DispositivoConfiavelService } from '../shared/dispositivo-confiavel.service';
import { EncryptService } from '../shared/encrypt.service';
import { ServiceBase } from '../shared/service-base';
import { SessionService } from '../shared/session.service';
import { AlterarSenhaMultiplasContasRequestEntity } from './entities/alterar-senha-multiplas-contas-request.entity';
import { UsuarioEntity } from './entities/usuario.entity';
import { ConsultarUsuariosCadastroSenhaResponseEntity } from './entities/responses/consultar-usuarios-cadastro-senha-response.entity';

export interface ValidarMfaResponse {
  emailValidado: boolean;
  celularValidado: boolean;
}

/** Resposta de v2/autenticacao/mfa/{hash}/confiar-dispositivo. Sem token quando o credor zerou o prazo. */
export interface ConfiarDispositivoResponse {
  tokenDispositivo?: string;
}

/** Resposta de v2/autenticacao/mfa/{hash}/reenviar. */
export interface ReenviarMfaResponse {
  /** Hash novo. O anterior deixa de valer no mesmo instante. */
  hashMfa: string;
  /** Quanto o backend vai recusar um novo pedido. O contador da tela usa este valor. */
  segundosParaNovoReenvio: number;
  reenviosRestantes: number;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService extends ServiceBase {
  constructor(
    @Optional() private auth: Auth,
    private http: HttpClient,
    private sessionService: SessionService,
    private encriptyService: EncryptService,
    private dispositivoConfiavel: DispositivoConfiavelService
  ) { super(); }

  consultarCodigoUsuarioEmailCpfCelular(identificacao: string): Observable<number> {
    return this.http.get<number>(`${environment.apiSeguranca}v2/autenticacao/${identificacao}`);
  }

  consultarCodigoUsuarioSemSenha(usuario: string): Observable<number> {
    return this.http.get<number>(`${environment.apiSeguranca}autenticacao/UsuarioPossuiSenhaNula?usuario=${usuario}`);
  }

  consultarGrupoCredorUsuario(hash: string): Observable<number> {
    return this.http.get<number>(`${environment.apiSeguranca}v2/autenticacao/grupocredor/${hash}`);
  }

  autenticarIdentificacaoSenha(usuario: UsuarioEntity): Observable<any> {
    const tokenDispositivo = this.dispositivoConfiavel.obter(usuario?.usuario);
    const securityEntry = this.encriptyService.encryptData(JSON.stringify({ ...usuario, tokenDispositivo }));
    return this.http.post<any>(`${environment.apiSeguranca}v2/autenticacao`, { hash: securityEntry });
  }

  validarMfa(hashMfa: string, token: string): Observable<ValidarMfaResponse> {
    return this.http.post<ValidarMfaResponse>(`${environment.apiSeguranca}v2/autenticacao/mfa/${hashMfa}/validar`, { token });
  }

  /** Só funciona logo depois de validarMfa com o mesmo hash, e uma vez. */
  confiarDispositivo(hashMfa: string): Observable<ConfiarDispositivoResponse> {
    return this.http.post<ConfiarDispositivoResponse>(`${environment.apiSeguranca}v2/autenticacao/mfa/${hashMfa}/confiar-dispositivo`, {});
  }

  /**
   * Pede um código novo. O backend invalida o anterior e devolve OUTRO hash — quem chamar tem que
   * guardar o `hashMfa` da resposta, senão a validação seguinte usa um hash que não existe mais.
   */
  reenviarMfa(hashMfa: string): Observable<ReenviarMfaResponse> {
    return this.http.post<ReenviarMfaResponse>(`${environment.apiSeguranca}v2/autenticacao/mfa/${hashMfa}/reenviar`, {});
  }

  validarContato(hashValidacao: string, codigoEmail: string, codigoCelular: string): Observable<void> {
    return this.http.post<void>(`${environment.apiSeguranca}v2/autenticacao/contato/${hashValidacao}/validar`,
      { codigoEmail, codigoCelular });
  }

  autenticarGoogle(): Observable<any> {
    const provider = new GoogleAuthProvider();

    provider.addScope('profile');
    provider.addScope('email');

    return from(signInWithPopup(this.auth, provider));
  }

  autenticarUsuarioFirebase(googleUid: string, email: string, codigoSistema: number): Observable<string> {
    return this.http.get<string>(`${environment.apiSeguranca}v2/autenticacao/AutenticarUsuarioFirebase?hash=${googleUid}&email=${email}&codigoSistema=${codigoSistema}`);
  }

  autoSignInApp(chaveSeguranca: string, hash: string, codigoApp: number, usuario: string, cnpj: string = null) {
    const params = cnpj ? `?cnpj=${cnpj}` : '';
    return this.http.get<number>(`${environment.apiSeguranca}autenticacao/AutoSignInApp/${chaveSeguranca}/${hash}/${codigoApp}/${usuario}${params}`);
  }

  enviarTokensValidacao(usuario: UsuarioEntity, tipo: string): Observable<any> {
    return this.http.post<any>(`${environment.apiSeguranca}autenticacao/EnviarTokensValidacao/${tipo}`, usuario);
  }

  validarEmailCelularUsuario(codigoUsuario: number): Observable<void> {
    return this.http.get<void>(`${environment.apiSeguranca}autenticacao/ValidarEmailCelularUsuario/${codigoUsuario}`);
  }

  /**
   * Dispara o e-mail com o link de definição de senha.
   * `primeiroAcesso` só muda o assunto do e-mail; o link e o token são os mesmos.
   */
  recuperarSenha(usuario: string, urlCallBack: string | null = null, primeiroAcesso = false): Observable<object> {
    return this.http.get<object>(`${environment.apiSeguranca}v2/autenticacao/recuperarSenha/${usuario}/${environment.codigoSistemaFeev}/${urlCallBack}?primeiroAcesso=${primeiroAcesso}`);
  }

  /**
   * Lista as contas que o link de recuperação vai alterar. Não consome o token — quem consome é a
   * troca de senha. Substitui o consultarUsuariosCadastroSenha nesta tela: antes a identificação
   * vinha do próprio link (cifrada com chave pública), agora só o servidor sabe de quem é o token.
   */
  consultarContasTokenSenha(token: string): Observable<ConsultarUsuariosCadastroSenhaResponseEntity[]> {
    return this.http.get<ConsultarUsuariosCadastroSenhaResponseEntity[]>(
      `${environment.apiSeguranca}Autenticacao/ConsultarContasTokenSenha?token=${encodeURIComponent(token)}`);
  }

  alterarSenhaMultiplasContas(entity: AlterarSenhaMultiplasContasRequestEntity): Observable<void> {
    return this.http.put<void>(`${environment.apiSeguranca}Autenticacao/alterarSenhaMultiplasContas/`, entity);
  }

  consultarUsuariosCadastroSenha(usuario: string): Observable<ConsultarUsuariosCadastroSenhaResponseEntity[]> {
    return this.http.get<ConsultarUsuariosCadastroSenhaResponseEntity[]>(`${environment.apiSeguranca}Autenticacao/ConsultarUsuariosCadastroSenha?usuario=${usuario}`);
  }

  consultarWhiteLabel(hostname: string, hostnameSSO: string): Observable<object> {
    return this.http.get<object>(`${environment.apiCadastro}whitelabel/hostname-sso?hostname=${hostname}&hostnamesso=${hostnameSSO}`);
  }

  consultarSituacaoCredor(codigoCredor: number): Observable<any> {
    return this.http.get<any>(`${environment.apiCadastro}Credor/ConsultarSituacaoCredor/${codigoCredor}`);
  }
}
